import { PaginationState } from '@tanstack/react-table';
import { parseAsBoolean, parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import React from 'react';

import { StandalonePagination } from '@/components/table/StandalonePagination';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Page, PageContent, PageTitleRow, PageTopBar } from '@/components/ui/page-layout';
import { ErrorBox, PageSpinner } from 'widgets';

import { useFundAssetAllocations } from './asset-allocation-queries';
import { FundAllocationCard } from './FundAllocationCard';

const DEFAULT_PAGE_SIZE = 50;

const filterParsers = {
    q: parseAsString.withDefault(''),
    uncategorized: parseAsBoolean.withDefault(false),
    overrides: parseAsBoolean.withDefault(false),
    holdings: parseAsBoolean.withDefault(false),
    page: parseAsInteger.withDefault(1),
    size: parseAsInteger.withDefault(DEFAULT_PAGE_SIZE),
};

export const FundAssetAllocationsView: React.FC = () => {
    const [{ q: search, uncategorized, overrides, holdings, page, size }, setFilters] = useQueryStates(filterParsers, {
        history: 'replace',
    });

    const pagination: PaginationState = { pageIndex: Math.max(0, page - 1), pageSize: size };

    const setSearch = (value: string) => setFilters({ q: value || null, page: null });
    const setBoolFlag = (key: 'uncategorized' | 'overrides' | 'holdings', value: boolean) =>
        setFilters({ [key]: value || null, page: null });
    const setPagination = (next: PaginationState) =>
        setFilters({
            page: next.pageIndex > 0 ? next.pageIndex + 1 : null,
            size: next.pageSize !== DEFAULT_PAGE_SIZE ? next.pageSize : null,
        });

    const { data, isLoading, isFetching, error } = useFundAssetAllocations({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        search,
        hasUncategorized: uncategorized,
        hasOverrides: overrides,
        hasHoldings: holdings,
    });

    const totalNr = data?.totalNr ?? 0;
    const allocations = data?.assetAllocations ?? [];

    return (
        <Page>
            <PageTopBar />
            <PageTitleRow title="Fund asset allocations" />

            <PageContent>
                <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
                    <div className="flex flex-wrap items-center gap-4">
                        <Input
                            type="text"
                            placeholder="Filter by symbol / cusip / security id / description…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-96"
                        />
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="has-uncategorized"
                                checked={uncategorized}
                                onCheckedChange={(checked) => setBoolFlag('uncategorized', checked === true)}
                            />
                            <Label htmlFor="has-uncategorized" className="text-sm">
                                has uncategorized
                            </Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="has-overrides"
                                checked={overrides}
                                onCheckedChange={(checked) => setBoolFlag('overrides', checked === true)}
                            />
                            <Label htmlFor="has-overrides" className="text-sm">
                                has overrides
                            </Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="has-holdings"
                                checked={holdings}
                                onCheckedChange={(checked) => setBoolFlag('holdings', checked === true)}
                            />
                            <Label htmlFor="has-holdings" className="text-sm">
                                has holdings
                            </Label>
                        </div>
                    </div>

                    <div className="flex min-h-0 flex-1 flex-col">
                        <div className="min-h-0 flex-1 overflow-auto">
                            {isLoading ? (
                                <PageSpinner />
                            ) : error ? (
                                <ErrorBox>Failed to load asset allocations: {String(error)}</ErrorBox>
                            ) : allocations.length === 0 ? (
                                <div className="rounded-md border bg-white py-12 text-center text-sm text-gray-400">
                                    No funds match the current filters.
                                </div>
                            ) : (
                                <div className="grid grid-cols-5 gap-3">
                                    {allocations.map((allocation) => (
                                        <FundAllocationCard key={allocation.sId} allocation={allocation} />
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="h-1 w-full shrink-0">
                            {isFetching && !isLoading && (
                                <div className="h-1 w-full animate-pulse bg-gradient-to-r from-gray-300 via-blue-500 to-gray-300" />
                            )}
                        </div>
                        <div className="shrink-0">
                            <StandalonePagination
                                pagination={pagination}
                                onPaginationChange={setPagination}
                                totalItems={totalNr}
                            />
                        </div>
                    </div>
                </div>
            </PageContent>
        </Page>
    );
};
